import { vars } from 'tosijs'

// One line of 13px monospace with room to breathe (the test status line).
const TEST_STATUS_HEIGHT = '28px'

export const liveExampleStyleSpec = {
  ':host': {
    '--tosi-example-height': '320px',
    '--code-editors-bar-bg': '#777',
    '--code-editors-bar-color': '#fff',
    '--widget-bg': '#fff8',
    '--widget-color': '#000',
    position: 'relative',
    display: 'flex',
    height: 'var(--tosi-example-height)',
    background: 'var(--background)',
    boxSizing: 'border-box',
    // CM tooltips escape this clip via tooltips({position:'fixed'}) in code-editor-cm.ts.
    overflow: 'hidden',
  },

  /*
  The outline is drawn round the PREVIEW and round the code, not round the whole example, so
  the toolbar column sits outside it, beside the example rather than in it. An outline (inset
  by its width), not an inset box-shadow: an outline paints over the element's children, so
  CodeMirror's surface cannot cover it, and it follows the radius.
  */
  ':host [part="example"]::part(content), :host .code-editors': {
    borderRadius: vars.spacing25,
    outline: '2px solid #8883',
    outlineOffset: '-2px',
  },

  // Local-edit indicator: an accent outline on the always-visible `<>` handle when
  // this example differs from its doc source. NB every `:host.STATE [part=…]` rule
  // in this file uses `> [part="example"] > [part="exampleWidgets"]`, not a bare
  // descendant: a light-DOM `:host` is a global `tosi-example …` selector, so a
  // descendant combinator would leak a CONTAINER example's state class onto a
  // NESTED example's widget (the self-hosting demo). The child chain pins each rule
  // to the example's own direct toolbar.
  ':host.-locally-edited > [part="example"]::part(fixture)': {
    outline: '2px solid var(--brand-color, #da1167)',
    outlineOffset: '1px',
  },

  ':host.-maximize': {
    position: 'fixed',
    left: '0',
    top: '0',
    /*
    The VISIBLE viewport (dvh), not 100vh: on iPhone Safari 100vh is the height without the
    browser toolbar, so the bottom of a full-screen example sat under it — hiding the Console
    tab's REPL input, which lives at the very bottom. (Same class of bug as #2460's menus.)
    right: 0 rather than width: 100vw, which also counts a desktop scrollbar.
    */
    height: '100dvh',
    right: '0',
    margin: '0 !important',
  },

  '.-maximize': {
    zIndex: 101,
  },

  ':host.-vertical': {
    flexDirection: 'column',
  },

  /*
  Code open in the page (`showInline`), stacked because the column is narrow: two panes need
  twice the height one did, or each gets half an example's worth and neither is usable.
  */
  ':host.-inline-code.-vertical:not(.-maximize)': {
    height: 'calc(var(--tosi-example-height) * 2)',
  },

  /*
  In the page the code pane is a few hundred pixels wide, and its tab bar has to hold the
  tabs AND the way out. So the code gets the larger share, and undo/redo (which are also in
  the example menu, and on the keyboard) give up their place to the menu and the close button.
  */
  ':host.-inline-code:not(.-maximize):not(.-vertical) > .code-editors': {
    flex: '1 1 60%',
  },
  ':host.-inline-code:not(.-maximize):not(.-vertical) > [part="example"]': {
    flex: '1 1 40%',
  },
  ':host.-inline-code:not(.-maximize) [part="undo"], :host.-inline-code:not(.-maximize) [part="redo"]':
    {
      display: 'none',
    },

  // `view: console`: the console sits where the preview would be, beside the code. Under the
  // toolbar (z-index 100), over the preview, which is still there for the code to write to.
  ':host.-console-docked > [part="example"] > .example-console': {
    position: 'absolute',
    inset: '0',
    zIndex: '5',
  },

  /*
  The test status line (component.ts, updateTestStatus): one clipped line along the bottom of
  the example pane, in the inline views only. The preview and a docked console make room for
  it, so it never covers the console's input or the last line of a rendering.
  */
  ':host [part="testStatus"]': {
    display: 'none',
  },
  ':host.-inline-code.-has-test-status:not(.-maximize) [part="testStatus"]': {
    display: 'block',
    position: 'absolute',
    left: '0',
    right: '0',
    bottom: '0',
    zIndex: '6',
    boxSizing: 'border-box',
    height: TEST_STATUS_HEIGHT,
    lineHeight: TEST_STATUS_HEIGHT,
    padding: `0 ${vars.spacing}`,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    fontFamily: 'var(--mono-font, monospace)',
    fontSize: '13px',
    background: 'var(--code-bg, #fdfdfd)',
    boxShadow: '0 -1px 0 var(--tosi-example-border-color, #8884)',
  },
  /*
  A failure is the thing to read, so beside a preview it wraps to show what went wrong
  (scrolling past a few lines). Beside a docked console it stays one line: the console's
  input sits right above it and must not be covered.
  */
  ':host.-inline-code.-has-test-status:not(.-maximize):not(.-console-docked) [part="testStatus"].test-fail':
    {
      height: 'auto',
      minHeight: TEST_STATUS_HEIGHT,
      maxHeight: `calc(${TEST_STATUS_HEIGHT} * 3)`,
      overflow: 'auto',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      lineHeight: '1.5',
      padding: `4px ${vars.spacing}`,
    },
  ':host.-inline-code.-has-test-status:not(.-maximize) .preview': {
    paddingBottom: `calc(${vars.spacing} + ${TEST_STATUS_HEIGHT})`,
  },
  ':host.-inline-code.-has-test-status.-console-docked:not(.-maximize) > [part="example"] > .example-console':
    {
      bottom: TEST_STATUS_HEIGHT,
      height: 'auto',
    },
  // The floating results panel would sit on top of the line that now says the same thing.
  ':host.-inline-code.-has-test-status:not(.-maximize):not(.-test-only) [part="testResults"]':
    {
      display: 'none',
    },

  ':host .layout-indicator': {
    transition: '0.5s ease-out',
    transform: 'rotateZ(270deg)',
  },

  ':host.-vertical .layout-indicator': {
    transform: 'rotateZ(180deg)',
  },

  // Maximize/restore icon toggle — scoped to the example's own toolbar so a
  // container example's -maximize doesn't hide a nested example's maximize icon.
  ':host.-maximize > [part="example"] > [part="exampleWidgets"] .hide-if-maximized, :host:not(.-maximize) > [part="example"] > [part="exampleWidgets"] .show-if-maximized':
    {
      display: 'none',
    },

  ':host [part="example"]': {
    flex: '1 1 50%',
    height: '100%',
    minWidth: '0',
  },
  // the preview scrolls sideways, not the toolbar with it
  ':host [part="example"]::part(content)': {
    overflowX: 'auto',
    overflowY: 'hidden',
  },

  ':host .preview': {
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    boxSizing: 'border-box',
    // Breathing room around rendered example content (border-box so height:100%
    // still fits). The border/radius now live on :host.
    padding: vars.spacing,
  },

  // The first rendered child often carries its own top margin (a heading, a
  // paragraph); drop it so it doesn't stack on top of the new padding.
  ':host .preview > :first-child': {
    marginTop: '0',
  },

  ':host .preview-error': {
    padding: '8px 12px',
    margin: '8px',
    background: '#fee',
    color: '#900',
    borderRadius: '4px',
    fontSize: '13px',
    fontFamily: 'system-ui, sans-serif',
    whiteSpace: 'pre-wrap',
  },

  // The pending-run spinner (a `run` dialect awaiting its result). A ring in the text colour;
  // `.still` (prefers-reduced-motion, set from JS) keeps the ring and drops the spin.
  ':host [part="running"]': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '32px',
    height: '32px',
    margin: '-16px 0 0 -16px',
    borderRadius: '50%',
    // a full translucent track with one coloured quarter: an even ring, not a crescent
    // (border, not box-shadow, because only a border has per-side colours to animate)
    border: '3px solid #8884',
    borderTopColor: 'var(--text-color, #444)',
    boxSizing: 'border-box',
    animation: 'tosi-example-spin 0.8s linear infinite',
    pointerEvents: 'none',
    zIndex: '50',
  },
  ':host [part="running"].still': {
    animation: 'none',
  },
  ':host [part="running"][hidden]': {
    display: 'none',
  },
  '@keyframes tosi-example-spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },

  /*
  The Console tab (example-console.ts): the run's logs scrolling above a REPL prompt pinned to
  the bottom, like a browser console. Lines are text nodes, so logged markup is shown, not run.
  */
  ':host .example-console': {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'var(--code-bg, #fdfdfd)',
    fontFamily: 'var(--mono-font, monospace)',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  ':host .example-console .console-lines': {
    flex: '1 1 auto',
    minHeight: '0',
    overflow: 'auto',
    padding: `${vars.spacing50} ${vars.spacing}`,
  },
  ':host .example-console .console-line': {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: '1px 0',
  },
  ':host .example-console .console-warn': {
    color: '#8a5a00',
    background: '#fff8e1',
  },
  ':host .example-console .console-error': {
    color: '#b00020',
    background: '#fdecea',
  },
  ':host .example-console .console-debug, :host .example-console .console-dropped':
    {
      opacity: '0.6',
    },
  // the REPL's own lines: what you typed, and what it returned
  ':host .example-console .console-input': {
    opacity: '0.75',
  },
  ':host .example-console .console-input::before': {
    content: '"› "',
  },
  // the JavaScript a line became in its dialect, behind a toggle
  ':host .example-console .console-js-toggle': {
    font: 'inherit',
    fontSize: '0.8em',
    lineHeight: '1',
    marginLeft: vars.spacing50,
    padding: `0 ${vars.spacing25}`,
    border: 'none',
    borderRadius: vars.spacing25,
    boxShadow: 'inset 0 0 0 1px currentColor',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
  },
  ':host .example-console .console-js': {
    margin: `${vars.spacing25} 0 ${vars.spacing25} ${vars.spacing}`,
    padding: '0',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  ':host .example-console .console-js[hidden]': {
    display: 'none',
  },
  ':host .example-console .console-result::before': {
    content: '"← "',
    opacity: '0.6',
  },
  ':host .example-console .console-prompt': {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'flex-start',
    gap: vars.spacing50,
    padding: `${vars.spacing50} ${vars.spacing}`,
    boxShadow: 'inset 0 1px 0 #0002',
  },
  ':host .example-console .console-field': {
    flex: '1 1 auto',
    resize: 'none',
    border: 'none',
    boxShadow: 'none',
    outline: 'none',
    padding: '0',
    margin: '0',
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
  },

  ':host [part="editors"]': {
    flex: '1 1 200px',
    height: '100%',
    position: 'relative',
  },

  /*
  The example toolbar: the fixture of the example's <tosi-affix>, a column beside the preview
  (inside along the bottom when it does not fit or the example is maximized). The affix lays
  out and colours the column; this wrapper steps aside (display: contents) so its controls
  are the column's items. Controls are neutral; only the tests check carries status.
  */
  ':host [part="exampleWidgets"]': {
    display: 'contents',
    color: 'var(--text-color)',
  },
  ':host [part="example"]::part(fixture)': {
    gap: '2px',
    padding: '4px 2px',
  },
  ':host [part="exampleWidgets"] button, :host [part="exampleWidgets"] label': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    padding: '0',
    margin: '0',
    border: '0',
    borderRadius: '4px',
    background: 'transparent',
    color: 'inherit',
    cursor: 'pointer',
  },
  ':host [part="exampleWidgets"] button:hover, :host [part="exampleWidgets"] label:hover':
    {
      background: 'color-mix(in srgb, currentColor 15%, transparent)',
    },
  // The doc site brand-colours bare <button>s by overriding --text-color ON the
  // button (a link affordance). Revert it here so the toolbar's icon buttons are
  // neutral, matching the check <label> (which the button rule never touched).
  ':host [part="exampleWidgets"] button': {
    '--text-color': 'inherit',
  },

  // Run-tests toggle: the native checkbox is hidden; its icon is full-colour when
  // tests are on and greyed + monochrome when off. --tests-enabled (0|1, set on
  // <body>) is global, so every example's toggle stays in sync with no JS.
  ':host [part="exampleWidgets"] .tests-toggle input': {
    display: 'none',
  },
  ':host [part="exampleWidgets"] .tests-toggle': {
    opacity: 'calc(0.4 + 0.6 * var(--tests-enabled, 0))',
    filter: 'grayscale(calc(1 - var(--tests-enabled, 0)))',
    transition: 'opacity 0.2s, filter 0.2s',
  },

  ':host .code-editors': {
    overflow: 'hidden',
    // Match the editor surface (neutral off-white; off-black in dark mode) instead
    // of a hardcoded white that wouldn't adapt.
    background: 'var(--code-bg, #fdfdfd)',
    position: 'relative',
    top: '0',
    right: '0',
    flex: '1 1 50%',
    height: '100%',
    flexDirection: 'column',
    zIndex: '10',
  },

  ':host .code-editors:not([hidden])': {
    display: 'flex',
  },

  ':host .code-editors > h4': {
    padding: '5px',
    margin: '0',
    textAlign: 'center',
    background: 'var(--code-editors-bar-bg)',
    color: 'var(--code-editors-bar-color)',
    cursor: 'move',
  },

  ':host button.transparent, :host .sizer': {
    width: '32px',
    height: '32px',
    lineHeight: '32px',
    textAlign: 'center',
    padding: '0',
    margin: '0',
  },

  ':host .sizer': {
    cursor: 'nwse-resize',
  },

  '@keyframes test-pulse': {
    '0%, 100%': { opacity: '1' },
    '50%': { opacity: '0.4' },
  },

  // Test status colours the tests check (--widget-color), which is neutral until tests run.
  ':host.-test-running > [part="example"] > [part="exampleWidgets"]': {
    '--widget-color': '#fa0',
  },
  ':host.-test-running > [part="example"] > [part="exampleWidgets"] > .tests-toggle':
    {
      animation: 'test-pulse 0.75s ease-in-out infinite',
    },

  ':host.-test-passed > [part="example"] > [part="exampleWidgets"]': {
    '--widget-color': '#0a0',
  },

  ':host.-test-failed > [part="example"] > [part="exampleWidgets"]': {
    '--widget-color': '#f00',
  },
  ':host:is(.-test-running, .-test-passed, .-test-failed) > [part="example"] > [part="exampleWidgets"] > .tests-toggle':
    {
      color: 'var(--widget-color)',
    },

  ':host [part="testResults"]': {
    position: 'absolute',
    bottom: '54px',
    left: '5px',
    background: 'var(--widget-bg)',
    borderRadius: '5px',
    padding: '8px',
    fontSize: '14px',
    margin: '0',
    maxWidth: '400px',
    maxHeight: '200px',
    overflow: 'auto',
    zIndex: '100',
  },

  ':host [part="testResults"][hidden]': {
    display: 'none',
  },

  /*
  A TEST-ONLY example: the results are the body, not an overlay.

  A ` ```test ` fence with no js/html/css beside it has nothing to render, so the preview was
  an empty box — which reads as a broken example rather than a passing suite, and hid the one
  thing the block exists to show. Two of these are in this project's own corpus.

  So the floating panel becomes in-flow content filling the example, and the empty preview
  stops reserving space.
  */
  ':host(.-test-only) [part="testResults"]': {
    position: 'static',
    maxWidth: 'none',
    maxHeight: 'none',
    background: 'transparent',
    padding: 'var(--spacing, 10px)',
    fontSize: 'inherit',
  },

  ':host(.-test-only) .preview': {
    display: 'none',
  },

  /*
  What a run logged, in place of a preview that rendered nothing (component.ts, refreshOutput).
  Same line classes as the Console tab, so a warning looks like a warning in both places. The
  empty preview stops reserving space but stays in the document: example code still owns it.
  */
  ':host [part="output"]': {
    boxSizing: 'border-box',
    padding: vars.spacing,
    maxHeight: '20em',
    overflow: 'auto',
    fontFamily: 'var(--mono-font, monospace)',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  ':host [part="output"] .console-line': {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
    padding: '1px 0',
  },
  ':host [part="output"] .console-warn': {
    color: '#8a5a00',
    background: '#fff8e1',
  },
  ':host [part="output"] .console-error': {
    color: '#b00020',
    background: '#fdecea',
  },
  ':host [part="output"] .console-debug, :host [part="output"] .console-dropped':
    {
      opacity: '0.6',
    },
  ':host(.-output-only) .preview': {
    height: 'auto',
    padding: '0',
  },

  ':host .test-pass': {
    color: '#0a0',
  },

  ':host .test-fail': {
    color: '#f00',
  },

  // Read-only "tjs tests" results tab (inline /*test*/ results).
  // The Docs tab (#184): generated documentation, read as prose, so the body font
  ':host .example-docs': {
    padding: `${vars.spacing50} ${vars.spacing}`,
    overflow: 'auto',
  },

  ':host .tjs-test-results': {
    padding: '8px 12px',
    fontSize: '14px',
    fontFamily: 'var(--mono-font, monospace)',
    overflow: 'auto',
    lineHeight: '1.6',
  },

  ':host .tjs-test-summary': {
    fontWeight: 'bold',
    marginBottom: '6px',
  },

  ':host .tjs-test-empty': {
    opacity: '0.6',
  },

  ':host .tjs-test-error': {
    opacity: '0.7',
  },
}
